import { useQuery } from "@tanstack/react-query";
import { list, request } from "./client";

/**
 * Ombor xaritasi (reporting-service).
 *
 * Geometriya KANONIK KODdan olinadi (TZ §5.1), koordinatadan
 * emas: `coordinate_x/y` marshrut masofasi uchun kiritiladi va
 * reyestrda ko'pincha bo'sh yoki shunchaki tartib raqami bo'lib
 * keladi. Kod esa manzilning o'zi — «A{qator}-{stellaj}-{qavat}-
 * {yacheyka}» to'rtta o'lchovni aniq beradi.
 *
 * Shuning uchun serverdan bo'lak (`section`) va undagi satr/ustun
 * TAYYOR holda keladi: ajratish bitta joyda — SQLda — bajariladi
 * va mijozda takrorlanmaydi.
 */

/** Chizma turi — bo'lak qanday ko'rsatilishini belgilaydi. */
export type MapKind = "RACK" | "FLOOR" | "OPERATIONAL" | "OTHER";

export interface MapSection {
  warehouse_code: string;
  zone_code: string;
  section: string;
  /** Qator (`A01`) — 3D ko'rinish shu birlik bilan ishlaydi. */
  aisle?: string;
  level?: number;
  kind: MapKind;
  cell_count: number;
  /** Ichida qoldiq turgan kataklar. «Band pallet o'rni» EMAS. */
  occupied_cells: number;
  free_cells: number;
  inactive_cells: number;
  blocked_cells: number;
  pallet_capacity: number;
  occupied_slots: number;
  /** Chizma o'lchami: panjara shu bo'yicha quriladi. */
  max_row: number;
  max_col: number;
}

export interface MapCell {
  location_id: string;
  code: string;
  zone_code: string;
  location_function: string;
  cell_type: string;
  abc_zone?: string;
  row?: number;
  col?: number;
  /** Qavat — 3D ko'rinish kataklarni shu bo'yicha ustma-ust qo'yadi. */
  level?: number;
  capacity_pallets?: number;
  occupied_slots: number;
  free_slots: number;
  /** Miqdor MATN sifatida (dev_spec.md §7.2). */
  on_hand_qty: string;
  distinct_sku_count: number;
  /**
   * Katakda TURGAN tovarlarning ABC toifasi, eng tezi bo'yicha.
   *
   * `abc_zone` dan boshqa narsa: u katakning loyihadagi hududi,
   * bu esa unda ayni damda nima turgani. Ikkisi mos kelmasa —
   * tovar noto'g'ri joyda.
   */
  sku_abc_class?: string;
  /** O'lchamlar, sm. "0" — reyestrda to'ldirilmagan. */
  length_cm: string;
  width_cm: string;
  height_cm: string;
  max_weight_kg: string;
  is_active: boolean;
  putaway_allowed: boolean;
  blocked: boolean;
}

export function useMapSections(warehouse?: string) {
  return useQuery({
    queryKey: ["map", "sections", warehouse ?? ""],
    queryFn: ({ signal }) =>
      list<MapSection>("reporting", "/api/v1/map/sections", { warehouse }, signal),
  });
}

export function useMapCells(section: string | null, warehouse?: string) {
  return useQuery({
    queryKey: ["map", "cells", section ?? "", warehouse ?? ""],
    enabled: Boolean(section),
    queryFn: ({ signal }) =>
      list<MapCell>(
        "reporting",
        "/api/v1/map/cells",
        { section: section ?? "", warehouse },
        signal,
      ),
  });
}

/**
 * Butun QATOR kataklari — 3D ko'rinish uchun.
 *
 * Bitta qavat emas: ombor xodimi «birinchi qatorga boraman» deb
 * o'ylaydi va stellaj bilan qavatni birgalikda ko'rishi kerak.
 */
export function useAisleCells(aisle: string | null, warehouse?: string) {
  return useQuery({
    queryKey: ["map", "aisle", aisle ?? "", warehouse ?? ""],
    enabled: Boolean(aisle),
    queryFn: ({ signal }) =>
      list<MapCell>("reporting", "/api/v1/map/cells", { aisle: aisle ?? "", warehouse }, signal),
  });
}

/**
 * Katakning holati — xaritada rangni shu belgilaydi.
 *
 * Tartib MUHIM: bitta katak bir vaqtda ham bloklangan, ham band
 * bo'lishi mumkin va bunda BLOK ustun turadi — u amalni
 * taqiqlaydi, bandlik esa shunchaki ma'lumot.
 */
export type CellState = "inactive" | "blocked" | "full" | "partial" | "empty" | "no-putaway";

export function cellState(cell: MapCell): CellState {
  if (!cell.is_active) return "inactive";
  if (cell.blocked) return "blocked";

  const hasStock = cell.occupied_slots > 0 || Number(cell.on_hand_qty) > 0;
  if (!hasStock) return cell.putaway_allowed ? "empty" : "no-putaway";

  // Pallet o'rni belgilangan bo'lsa to'lganini ANIQ aytish mumkin.
  // Y1/Y2 mayda terish yacheykalarida o'rin tushunchasi yo'q
  // (TZ §5.2) — ularda faqat "qoldiq bor" deyish halolroq.
  if (cell.capacity_pallets != null && cell.free_slots <= 0) return "full";
  return "partial";
}

export const CELL_STATE_LABEL: Record<CellState, string> = {
  empty: "Bo'sh",
  partial: "Qisman band",
  full: "To'la",
  blocked: "Bloklangan",
  inactive: "Nofaol",
  "no-putaway": "Joylashtirish yopiq",
};

/**
 * Vazifa (MX, OX…) kesimidagi yig'indi.
 *
 * FOIZ serverdan KELMAYDI: «bloklangan ulushi» kataklardan,
 * «to'ldirilish» esa pallet o'rinlaridan hisoblanadi — maxrajlari
 * boshqacha va ikkalasini bitta javobga solish ularni
 * adashtirishga olib kelardi.
 */
export interface MapSummaryRow {
  function: string;
  cell_count: number;
  occupied_cells: number;
  inactive_cells: number;
  blocked_cells: number;
  pallet_capacity: number;
  occupied_slots: number;
}

export function useMapSummary(warehouse?: string) {
  return useQuery({
    queryKey: ["map", "summary", warehouse],
    queryFn: ({ signal }) =>
      list<MapSummaryRow>("reporting", "/api/v1/map/summary", { warehouse }, signal),
  });
}

/**
 * Issiq nuqtalar: katakka oynada necha marta tegilgani.
 *
 * Harakati BO'LMAGAN katak ro'yxatda YO'Q — reyestrda 15 mingdan
 * ortiq yacheyka bor va nollarni uzatish javobni bekorga
 * kattalashtirardi. Chizma ro'yxatda yo'q katakni «sovuq» deb
 * chizadi.
 */
export interface MapHeatPoint {
  location_id: string;
  /** Kirish va chiqish harakatlarining yig'indisi. */
  moves: number;
  /** Shundan terish amallari. */
  picks: number;
}

/**
 * Rang shkalasi — BUTUN KORPUS bo'yicha, ko'rinayotgan
 * kataklardan emas: shunda bir qatordagi qizil boshqa qatordagi
 * qizil bilan bir xil ma'noni bildiradi.
 */
export interface HeatScale {
  /** Oynada harakati bo'lgan kataklar soni. */
  cells: number;
  max: number;
  /** Harakati BO'LGAN kataklar orasidagi mediana va 90-protsentil. */
  median: number;
  p90: number;
}

export interface MapHeatResponse {
  window_days: number;
  scale: HeatScale;
  items: MapHeatPoint[];
}

export const EMPTY_HEAT_SCALE: HeatScale = { cells: 0, max: 0, median: 0, p90: 0 };

/**
 * Harakat so'rovi ALOHIDA: oyna o'zgarganda faqat shu ro'yxat
 * qayta so'raladi, butun chizma emas. Katak ro'yxati esa
 * oynaga bog'liq emas va o'z keshida qoladi.
 */
export function useMapHeat(
  section: string | null,
  aisle: string | null,
  warehouse: string | undefined,
  days: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["map", "heat", section ?? "", aisle ?? "", warehouse ?? "", days],
    enabled: enabled && Boolean(section || aisle),
    queryFn: ({ signal }) =>
      request<MapHeatResponse>("reporting", "/api/v1/map/heat", {
        query: {
          section: section ?? undefined,
          aisle: aisle ?? undefined,
          warehouse,
          days,
        },
        signal,
      }),
  });
}

/**
 * Katak tarkibi: unda qanday tovar turibdi.
 *
 * Alohida so'rov: chizmada 15 mingta katak bor va har birining
 * tarkibini oldindan yuklash javobni yuzlab barobar
 * kattalashtirardi. Tarkib faqat katak BOSILGANDA so'raladi.
 */
export interface CellContentRow {
  sku_id: string;
  article: string;
  name: string;
  /** Miqdorlar MATN sifatida (dev_spec.md §7.2). */
  on_hand_qty: string;
  reserved_qty: string;
  lp_count: number;
  lot_count: number;
  /**
   * Shu qoldiqning og'irligi va hajmi.
   *
   * `has_vgh` false bo'lsa ular NOL va ularni o'qib bo'lmaydi —
   * interfeys «VGH yo'q» deb yozishi kerak. Nolni «og'irligi
   * yo'q» deb ko'rsatish yuk chegarasi bo'yicha noto'g'ri qaror
   * berardi.
   */
  weight_kg: string;
  volume_cm3: string;
  has_vgh: boolean;
}

export interface CellContents {
  items: CellContentRow[];
  total_skus: number;
  /** Og'irligi va hajmi MA'LUM bo'lgan tovarlar soni. */
  measured_skus: number;
  total_qty: string;
  total_weight_kg: string;
  total_volume_cm3: string;
}

export function useCellContents(locationID: string | null) {
  return useQuery({
    queryKey: ["map", "contents", locationID ?? ""],
    enabled: Boolean(locationID),
    queryFn: ({ signal }) =>
      request<CellContents>("reporting", `/api/v1/map/cells/${locationID}/contents`, { signal }),
  });
}
